import moment from 'moment';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TableState } from '../components/ui/Table';
import { URole } from '../constants/navigation';
import useAPI from '../hooks/useAPI';
import { TransactionReasponse } from '../lib/types/Transaction';
import { IWalletTransaction } from '../pages/Wallet';
import AuthContext from './AuthContext';

interface WalletContextType {
  walletData: any;
  setWalletData: (data: any) => void;
  transactionData: IWalletTransaction[];
  setTransactionData: React.Dispatch<
    React.SetStateAction<IWalletTransaction[]>
  >;
  transactionHistory: TransactionReasponse | null;
  setTransactionHistory: React.Dispatch<
    React.SetStateAction<TransactionReasponse | null>
  >;
  isLoading: boolean;
  isTransactionLoading: boolean;
  hasMoreTransactions: boolean;
  page: number;
  tableState: TableState;
  refetch: () => void;
  refetchTransactions: () => void;
  loadMoreTransactions: () => void;
  updateTableState: (newState: TableState) => void;
  setStatus: Dispatch<SetStateAction<string>>;
  setType: Dispatch<SetStateAction<string>>;
  setDate: Dispatch<SetStateAction<string>>;
  setPage: Dispatch<SetStateAction<number>>;
  status: string;
  type: string;
  date: string;
  startDate: Date | undefined;
  setStartDate: Dispatch<SetStateAction<Date | undefined>>;
  endDate: Date | undefined;
  setEndDate: Dispatch<SetStateAction<Date | undefined>>;
  dateRange: [Date | null, Date | null];
  setDateRange: Dispatch<SetStateAction<[Date | null, Date | null]>>;
}

const WalletContext = createContext<WalletContextType>({
  walletData: null,
  setWalletData: () => {},
  transactionData: [],
  setTransactionData: () => {},
  transactionHistory: null,
  setTransactionHistory: () => {},
  isLoading: false,
  isTransactionLoading: false,
  hasMoreTransactions: true,
  page: 1,
  tableState: { sort: { column: null, direction: null }, filters: {} },
  refetch: () => {},
  refetchTransactions: () => {},
  loadMoreTransactions: () => {},
  updateTableState: () => {},
  setStatus: () => {},
  setType: () => {},
  setDate: () => {},
  status: 'All',
  type: 'All',
  date: 'All',
  setPage: () => {},
  startDate: undefined,
  setStartDate: () => {},
  endDate: undefined,
  setEndDate: () => {},
  dateRange: [null, null],
  setDateRange: () => {},
});

export default WalletContext;

export const WalletProvider = ({ children }: { children: React.ReactNode }) => {
  const [walletData, setWalletData] = useState<any>(null);
  const [transactionData, setTransactionData] = useState<IWalletTransaction[]>(
    [],
  );
  const [transactionHistory, setTransactionHistory] =
    useState<TransactionReasponse | null>(null);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true);
  const [page, setPage] = useState(1);
  const [tableState, setTableState] = useState<TableState>({
    sort: { column: null, direction: null },
    filters: {},
  });
  const [status, setStatus] = useState('All');
  const [type, setType] = useState('All');
  const [date, setDate] = useState('All');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const location = useLocation();
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    setDateRange([null, null]);
    setEndDate(undefined);
    setStartDate(undefined);
    setType('All');
    setStatus('All');
  }, [location]);

  const { userRole } = useContext(AuthContext);

  useEffect(() => {
    if (endDate !== null || type || status) {
      setPage(1);
    }
  }, [endDate, type, status]);

  // Wallet Balance API
  const { isLoading, refetch } = useAPI({
    url: '/auth/wallet/balance/user',
    method: 'GET',
    enabled: !!userRole && userRole !== URole.COMPANY_ADMIN,
    onSuccess: data => {
      setWalletData(data?.result);
    },
    onError: () => {},
  });
  const {
    data,
    isLoading: isTransactionLoading,
    refetch: refetchTransactions,
  } = useAPI({
    url: `/token/transaction/history/get?page=${page}&limit=10&status=${status ? (status === 'All' ? '' : status) : (tableState?.filters?.status?.value ?? '')}&type=${type ? (type === 'All' ? '' : type) : (tableState?.filters?.type?.value ?? '')}&from=${startDate ? moment(startDate).format('YYYY-MM-DD') : ''}&to=${endDate ? moment(endDate).format('YYYY-MM-DD') : ''}`,
    dependencies: [page, endDate, status, type],
    method: 'GET',
    onSuccess: data => {
      // console.log(data, 'userData');
      const newData = data?.result?.tokenTransactionHistory.map(
        (item: any) => ({
          ...item,
        }),
      );

      setTransactionData(prevData => {
        if (page === 0) return newData;

        // Merge without duplicates based on transactionId
        const existingIds = new Set(prevData.map(item => item.id));
        const uniqueNewTransactions = newData.filter(
          (item: any) => !existingIds.has(item.id),
        );

        return [...prevData, ...uniqueNewTransactions];
      });

      setTransactionData(prevData => {
        return prevData.sort((a: any, b: any) => {
          const dateA = moment(a.transactionDate);
          const dateB = moment(b.transactionDate);

          if (dateA.isBefore(dateB)) return 1;
          if (dateA.isAfter(dateB)) return -1;
          return 0;
        });
      });

      setHasMoreTransactions(data?.result?.totalPages > page);
    },
    onError: error => {
      if (status !== 'All' || type !== 'All') {
        toast.error(error?.response?.data?.message);
      } else {
        toast.error('Network error. Please check your connection and retry.');
      }
    },
  });

  const loadMoreTransactions = () =>
    !isLoading &&
    page < data.result.totalPages &&
    setPage(prePage => prePage + 1);

  const updateTableState = (newState: TableState) => {
    setTableState(newState);
    setPage(1);
  };

  return (
    <WalletContext.Provider
      value={{
        walletData,
        setWalletData,
        transactionData,
        setTransactionData,
        transactionHistory,
        setTransactionHistory,
        isLoading,
        isTransactionLoading,
        hasMoreTransactions,
        page,
        tableState,
        refetch,
        refetchTransactions,
        loadMoreTransactions,
        updateTableState,
        setStatus,
        setType,
        setDate,
        status,
        type,
        date,
        setPage,
        dateRange,
        setDateRange,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};
